// components/PaymentInputView.jsx
import { useState } from 'react';
import styled from 'styled-components';
import { BodyText } from '../../../../components/ui';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  background: ${({ theme }) => theme.colors.white};
  margin-bottom: 20px;
  margin-right: 0;
`;

const Section = styled.div`
  width: 100%;
  max-width: 100%;
  margin-bottom: 20px;
  border: 2px solid ${({ theme }) => theme.colors.subBackground};
  border-radius: 10px;
  padding: 5px 10px;
  box-sizing: border-box;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
`;

const Title = styled.h3`
  padding: 0 10px;
  font-size: ${({ theme }) => theme.fontSizes.medium};
  color: ${({ theme }) => theme.colors.primaryText};
`;

const ToggleIcon = styled.span`
  padding: 0 10px;
  font-size: 18px;
`;

const AddressItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 100%;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 10px;
  background-color: ${({ selected, theme }) =>
    selected ? theme.colors.subBackground : theme.colors.white};
  cursor: pointer;
  box-sizing: border-box;
`;

const Radio = styled.input.attrs({ type: 'radio' })`
  margin-right: 20px;
`;

const PaymentMethodItem = styled(AddressItem)`
  gap: 10px;
`;

const Row = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
  max-width: 100%;
  box-sizing: border-box;
`;

const CardLogo = styled.div`
  background-color: ${({ bg }) => bg};
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: bold;
  padding: 4px 8px;
  border-radius: 4px;
  min-width: 50px;
  text-align: center;
`;

const cardColors = {
  v: {
    bg: '#1A1F71',
    text: 'VISA',
  },
  m: {
    bg: '#EB001B',
    text: 'Master',
  },
  a: {
    bg: '#2E77BC',
    text: 'AMEX',
  },
  d: {
    bg: '#FF6000',
    text: 'DISCOVER',
  },
  default: {
    bg: '#CCCCCC',
    text: 'CREDIT CARD',
  },
};

const PaymentInputView = ({
  shipToAddressList,
  selectedShipToAddressId,
  onSelectShipToAddress,
  billToAddressList,
  selectedBillToAddressId,
  onSelectBillToAddress,
  paymentMethodList,
  selectedPaymentId,
  onSelectPayment,
}) => {
  const [openSection, setOpenSection] = useState({
    shipTo: true,
    billTo: true,
    paymentMethod: true,
  });

  const toggleSection = (section) => {
    setOpenSection((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const renderCardImage = (type) => {
    if (type === 'term') {
      return <CardLogo bg={'#5F6FB2'}>TERM</CardLogo>;
    }

    const cardType = type?.toLowerCase();
    const cardInfo = cardColors[cardType] || cardColors.default;
    const displayText = cardInfo.text;

    return <CardLogo bg={cardInfo.bg}>{displayText}</CardLogo>;
  };

  const renderShipToAddressItem = (id, list) => {
    const selected = list.find((l) => l.idAddr === id);

    if (selected) {
      return (
        <div>
          <BodyText weight={'bold'}>
            {[
              selected.companyNm,
              selected.addr,
              selected.city,
              selected.states,
              selected.zipcode,
              selected.country,
            ]
              .filter(Boolean)
              .join(', ')}
          </BodyText>
        </div>
      );
    }
  };

  const renderBillToAddressItem = (id, list) => {
    const selected = list.find((l) => l.idAddr === id);

    if (selected) {
      return (
        <div>
          <BodyText weight={'bold'}>
            {[
              selected.companyNm,
              selected.addr,
              selected.city,
              selected.states,
              selected.zipcode,
              selected.country,
            ]
              .filter(Boolean)
              .join(', ')}
          </BodyText>
        </div>
      );
    }
  };

  const renderCardItem = (pm) => {
    if (pm.isPaymentTerm) {
      return (
        <Row>
          <Radio checked={pm.idCC === selectedPaymentId} readOnly />
          {renderCardImage('term')}
          <div>{pm.label}</div>
        </Row>
      );
    }

    if (pm.idCC == -1) {
      return (
        <Row>
          <Radio checked={pm.idCC === selectedPaymentId} readOnly />
          {renderCardImage(pm.a5)}
          <div>{pm.a2}</div>
        </Row>
      );
      // cash
    } else {
      return (
        <Row>
          <Radio checked={pm.idCC === selectedPaymentId} readOnly />
          {renderCardImage(pm.a5)}
          <div>
            <div>{`${pm.a2} •••• ${pm.a1}`}</div>
            <div style={{ color: '#999', fontSize: '0.8rem' }}>
              {pm.billAddr}
            </div>
          </div>
        </Row>
      );
    }
  };

  const renderSelectedCard = (id, list) => {
    const selected = list.find((l) => l.idCC === id);
    if (selected) {
      if (selected.isPaymentTerm) {
        return (
          <Row>
            {renderCardImage('term')}
            <div>{selected.label}</div>
          </Row>
        );
      }

      return (
        <Row>
          {renderCardImage(selected.a5)}
          <div>{`${selected.a2} •••• ${selected.a1}`}</div>
          <div style={{ color: '#999', fontSize: '0.8rem' }}>
            {selected.billAddr}
          </div>
        </Row>
      );
    }
  };

  return (
    <Container>
      <Section>
        <SectionHeader onClick={() => toggleSection('shipTo')}>
          <Row>
            <Title>Ship to</Title>
            {!openSection.shipTo && shipToAddressList
              ? renderShipToAddressItem(
                  selectedShipToAddressId,
                  shipToAddressList,
                )
              : ''}
          </Row>
          <ToggleIcon>{openSection.shipTo ? '▲' : '▼'}</ToggleIcon>
        </SectionHeader>
        {openSection.shipTo && shipToAddressList && (
          <>
            {shipToAddressList.map((addr) => {
              return (
                <AddressItem
                  key={addr.idAddr}
                  selected={addr.idAddr === selectedShipToAddressId}
                  onClick={() => onSelectShipToAddress(addr.idAddr)}
                >
                  <Row>
                    <Radio
                      checked={addr.idAddr === selectedShipToAddressId}
                      readOnly
                    />
                    <div>
                      <div>
                        <strong>{addr.companyNm || ''}</strong>
                      </div>
                      <div>
                        {[
                          addr.addr,
                          addr.city,
                          addr.states,
                          addr.zipcode,
                          addr.country,
                        ]
                          .filter(Boolean)
                          .join(', ')}
                      </div>
                    </div>
                  </Row>
                  {/* {openSection.shipTo && (
                    <DeleteButton onClick={() => removeDbAddress(addr.idAddr)}>
                      <FaRegTrashAlt size={'18px'} color={theme.colors.white} />
                    </DeleteButton>
                  )} */}
                </AddressItem>
              );
            })}
          </>
        )}
        {/* {openSection.shipTo && (
          <AddButton onClick={onAddShipToAddress}>
            + Use a different address
          </AddButton>
        )} */}
      </Section>

      <Section>
        <SectionHeader onClick={() => toggleSection('billTo')}>
          <Row>
            <Title>Bill to</Title>
            {!openSection.billTo && billToAddressList
              ? renderBillToAddressItem(
                  selectedBillToAddressId,
                  billToAddressList,
                )
              : ''}
          </Row>
          <ToggleIcon>{openSection.billTo ? '▲' : '▼'}</ToggleIcon>
        </SectionHeader>
        {openSection.billTo &&
          billToAddressList &&
          billToAddressList.map((addr) => (
            <AddressItem
              key={addr.idAddr}
              selected={addr.idAddr === selectedBillToAddressId}
              onClick={() => onSelectBillToAddress(addr.idAddr)}
            >
              <Row>
                <Radio
                  checked={addr.idAddr === selectedBillToAddressId}
                  readOnly
                />
                <div>
                  <div>
                    <strong>{addr.companyNm || ''}</strong>
                  </div>
                  <div>
                    {[
                      addr.addr,
                      addr.city,
                      addr.states,
                      addr.zipcode,
                      addr.country,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </div>
                </div>
              </Row>
              {/* {openSection.billTo && (
                  <DeleteButton onClick={() => removeDbAddress(addr.idAddr)}>
                    <FaRegTrashAlt size={'18px'} color={theme.colors.white} />
                  </DeleteButton>
                )} */}
            </AddressItem>
          ))}
        {/* {openSection.billTo && (
          <AddButton onClick={onAddBillToAddress}>
            + Use a different address
          </AddButton>
        )} */}
      </Section>

      {/* Payment Section */}
      <Section>
        <SectionHeader onClick={() => toggleSection('paymentMethod')}>
          <Row>
            <Title>Payment Type</Title>
            {!openSection.paymentMethod && paymentMethodList
              ? renderSelectedCard(selectedPaymentId, paymentMethodList)
              : ''}
          </Row>
          <ToggleIcon>{openSection.paymentMethod ? '▲' : '▼'}</ToggleIcon>
        </SectionHeader>
        {openSection.paymentMethod &&
          paymentMethodList &&
          paymentMethodList.map((pm) => (
            <PaymentMethodItem
              key={pm.idCC}
              selected={pm.idCC === selectedPaymentId}
              onClick={() => onSelectPayment(pm.idCC)}
            >
              {renderCardItem(pm)}
              {/* {openSection.paymentMethod && (
                <DeleteButton
                  onClick={() => {
                    removeDbPaymentMethod(pm.idCC);
                  }}
                >
                  <FaRegTrashAlt size={'18px'} color={theme.colors.white} />
                </DeleteButton>
              )} */}
            </PaymentMethodItem>
          ))}
        {/* {openSection.paymentMethod && (
          <AddButton onClick={onAddPayment}>
            + Add a debit or credit card
          </AddButton>
        )} */}
      </Section>
    </Container>
  );
};

export default PaymentInputView;
